import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Loader2, Sparkles, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import { getErrorMessage } from '../lib/errors';
import { authApi } from '../lib/api';
import { ThemeToggle, Logo } from '../components/Layout';
import { trackEvent, EVENTS } from '../lib/analytics';
import { GuestCheckoutDialog } from '../components/billing/GuestCheckoutDialog';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  // "Buy Now" path for new visitors who landed on the login screen.
  // Replaces the former "Create an account" signup link.
  const [buyOpen, setBuyOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // If the user just paid via the guest checkout, we route here with
  // ``?paid=1&email=<what-they-typed-in-checkout>``. Pre-fill the email
  // so it's *literally impossible* to type a different one and end up
  // logging into a different (unpaid) account.
  const justPaid = searchParams.get('paid') === '1';
  const paidEmail = searchParams.get('email') || '';

  useEffect(() => {
    if (paidEmail && !email) setEmail(paidEmail);
  }, [paidEmail, email]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      trackEvent(EVENTS.LOGIN, { method: 'email' });
      toast.success('Welcome back!');
      // Dashboard reads any free-score pendingAudit directly from
      // localStorage on mount — routing via ?autostart_url races with
      // PublicOnly's <Navigate to="/dashboard"> so we let the Dashboard
      // handle the handoff itself.
      navigate('/dashboard');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const onForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotSubmitting(true);
    try {
      await authApi.requestPasswordReset(forgotEmail.trim().toLowerCase());
      toast.success("If we recognize that email, an admin will reset your password and contact you within 24 hours.");
      setForgotOpen(false);
      setForgotEmail('');
    } catch (err) {
      // Backend always returns 200 to avoid leaking which emails exist, but
      // surface a generic message in case the network itself fails.
      toast.message("Request received. If we recognize that email, an admin will be in touch.");
      setForgotOpen(false);
      setForgotEmail('');
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <aside className="hidden lg:flex relative flex-col p-10 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 hero-mesh-light opacity-20" />
        <div className="relative">
          <Logo />
          <div className="mt-16">
            <h2 className="text-3xl font-bold leading-tight">Welcome back, creator.</h2>
            <p className="mt-3 opacity-90 max-w-md">
              Pick up where you left off — your past analyses, voice profile, and posts are waiting.
            </p>
          </div>
        </div>
        <p className="relative mt-auto text-sm opacity-80">
          “I rewrote my profile in 20 minutes and got 3 inbound DMs in a week.”
        </p>
      </aside>
      <main className="flex flex-col">
        <div className="flex items-center justify-between p-4 lg:hidden">
          <Logo />
          <ThemeToggle />
        </div>
        <div className="hidden lg:flex justify-end p-4">
          <ThemeToggle />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <Card className="w-full max-w-md border-border">
            <CardContent className="p-7">
              <h1 className="text-2xl font-bold">Log in</h1>
              <p className="text-sm text-muted-foreground mt-1">Welcome back to Blayzrr.</p>

              {/* Reminder for buyers who paid via Razorpay checkout — the
                  account was created using the exact email they entered
                  there. Using a different email here creates a *second*
                  account with no active plan attached. When they arrive
                  from the just-completed guest checkout we bump the tone
                  and colour the message green so it reads like success +
                  next-step, not a warning. */}
              {justPaid ? (
                <div
                  className="mt-5 rounded-lg border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 p-3 flex items-start gap-2.5"
                  data-testid="login-paid-notice"
                >
                  <Sparkles size={16} className="text-[hsl(var(--success))] shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="text-[12px] leading-relaxed text-foreground/90">
                    <span className="font-semibold text-[hsl(var(--success))]">Payment received!</span>{' '}
                    We&apos;ve emailed your temporary password to{' '}
                    <span className="font-semibold text-foreground">{paidEmail || 'your inbox'}</span>.
                    Check your inbox (and spam folder), then log in below — we&apos;ve
                    pre-filled the same email you paid with so you land in the right account.
                  </div>
                </div>
              ) : (
                <div
                  className="mt-5 rounded-lg border border-primary/30 bg-primary/5 p-3 flex items-start gap-2.5"
                  data-testid="login-paid-notice"
                >
                  <Info size={16} className="text-primary shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="text-[12px] leading-relaxed text-foreground/90">
                    <span className="font-semibold text-primary">Paid via Razorpay?</span>{' '}
                    Log in with the <span className="font-semibold">exact same email</span> you
                    used at checkout. We emailed your temporary password to that address —
                    check your inbox (and spam folder).
                  </div>
                </div>
              )}

              <form onSubmit={onSubmit} className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="pl-9 h-11"
                      data-testid="auth-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                      className="text-xs text-primary hover:underline underline-offset-4"
                      data-testid="auth-forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-9 h-11"
                      data-testid="auth-password-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 rounded-xl" disabled={loading} data-testid="auth-submit-button">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : 'Log in'}
                </Button>
              </form>
              <p className="text-sm text-muted-foreground mt-6 text-center">
                New to Blayzrr?{' '}
                <button
                  type="button"
                  onClick={() => setBuyOpen(true)}
                  className="text-primary font-semibold hover:underline"
                  data-testid="auth-buy-now-link"
                >
                  Buy Now — ₹999
                </button>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog open={forgotOpen} onOpenChange={(o) => !forgotSubmitting && setForgotOpen(o)}>
        <DialogContent className="sm:max-w-md" data-testid="auth-forgot-password-dialog">
          <form onSubmit={onForgotSubmit}>
            <DialogHeader>
              <DialogTitle>Reset your password</DialogTitle>
              <DialogDescription>
                Enter the email you used at signup. We&apos;ll email you a
                secure one-hour link to pick a new password &mdash; no admin
                wait needed. Check your spam folder if you don&apos;t see it
                in a minute.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5 mt-4">
              <Label htmlFor="forgot-email">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-3 text-muted-foreground" />
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-9 h-11"
                  autoFocus
                  data-testid="auth-forgot-email-input"
                />
              </div>
            </div>
            <DialogFooter className="mt-5">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setForgotOpen(false)}
                disabled={forgotSubmitting}
                data-testid="auth-forgot-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={forgotSubmitting || !forgotEmail.trim()}
                data-testid="auth-forgot-submit"
              >
                {forgotSubmitting ? <Loader2 size={14} className="animate-spin" /> : 'Send reset request'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <GuestCheckoutDialog open={buyOpen} onOpenChange={setBuyOpen} />
    </div>
  );
}
